import { useEffect, useMemo, useState } from 'react';

/**
 * 🔴 THE DECODED SIZE OF A PHOTO, WHICH IS THE ONLY THING THAT KNOWS ITS
 * ORIENTATION.
 *
 * Read `resolveFramingSource`'s header in `util/adFraming.js` first — it states
 * the defect this exists to close and carries the live measurements. In one
 * line: Sharetribe's variant `width`/`height` describe the STORED buffer, the
 * browser applies the EXIF orientation flag when it decodes, and for a portrait
 * phone photo the two are exact transposes. `framedImageStyle` needs the
 * decoded one.
 *
 * Why an off-DOM `new Image()` rather than an `onLoad` on the element we already
 * render: the geometry is needed to COMPUTE that element's style, so reading it
 * off the element is circular — the first paint would be unframed and then jump.
 * The browser cache makes the second request free (identical URL, already
 * in-flight or complete), and `img.complete` short-circuits synchronously for a
 * photo the page has already shown, which is the common case for the tiles and
 * for re-selecting a photo in the framer.
 *
 * 🔴 THE CACHE IS MODULE-LEVEL AND DELIBERATELY NEVER EVICTED. It is keyed by
 * URL and holds two integers per entry; a listing has at most 15 photos and a
 * session at most a handful of listings. What it buys is that re-selecting a
 * photo, a form re-initialisation, or a panel remount resolves SYNCHRONOUSLY
 * from the lazy initialiser below — so the framed geometry never flickers back
 * through the un-decoded fallback.
 *
 * ⚠️ jsdom does not load images: `naturalWidth` stays 0 and `load` never fires,
 * so every consumer degrades to the variant metadata under test exactly as it
 * degrades before the decode lands in production. Tests that need the corrected
 * geometry must seed `__setDecodedImageSizeForTests` rather than wait.
 */
const cache = new Map();

const usable = size =>
  !!size && Number.isFinite(size.width) && Number.isFinite(size.height) && size.width > 0;

/** Synchronous read — `null` when this URL has not been decoded yet. */
export const readDecodedImageSize = url => (url ? cache.get(url) || null : null);

/**
 * Test seam. Production never calls this; it exists so a jsdom test can pin the
 * CORRECTED geometry without pretending an image loaded.
 */
export const __setDecodedImageSizeForTests = (url, size) => {
  if (!url) {
    return;
  }
  if (size === null) {
    cache.delete(url);
  } else if (usable(size)) {
    cache.set(url, { width: size.width, height: size.height });
  }
};

/** Test seam — drop everything, so one test's seed cannot leak into the next. */
export const __clearDecodedImageSizesForTests = () => cache.clear();

/**
 * 🔴 Story 12.15 (AC-5b′) — WRITE A DECODE THAT CAME FROM A RENDERED `<img>`,
 * NOT FROM A PROBE. Production DOES call this, unlike the test seams above.
 *
 * The hooks below acquire the decode with an off-DOM `new Image()`, which is
 * right for a wizard strip of ≤15 already-loaded photos and WRONG for a
 * 24-card lazy-loaded search grid: it fires every probe in a mount `useEffect`,
 * bypassing `lazyLoadWithDimensions` entirely and downloading a variant the
 * browser may never have picked (`ResponsiveImage` emits `srcSet` with `w`
 * descriptors, so the chosen candidate is not knowable from JS). On the
 * marketplace's busiest page that is ~24 downloads pulled to first paint.
 *
 * A card instead reads `naturalWidth`/`naturalHeight` off its OWN `<img>`'s
 * `onLoad` and writes the answer here. Zero extra bytes at any DPR, no
 * mount-time burst, nothing pulled ahead of the lazy-loader — and it measures
 * THE PIXELS THE BROWSER ACTUALLY PAINTED, which is strictly more correct than
 * probing a variant it may not have chosen.
 *
 * It is the SAME cache, so `readDecodedImageSize` and every existing consumer
 * keep working unchanged, and a photo already decoded by the gallery or the
 * tiles still resolves synchronously with no flicker.
 *
 * ⚠️ The value may come from a DIFFERENT derivative than the key names — a
 * DPR-1 client paints `frame-1x` while `cardFramingVariant` resolved
 * `frame-2x`. That is SAFE for `framedImageStyle`, which is scale-invariant and
 * only reads the aspect (see `resolveFramingSource`'s header), and it is NOT
 * safe for `maxZoomForPhoto`, which needs that variant's own pixel count. No
 * card calls `maxZoomForPhoto`; do not make one without re-reading this note.
 */
export const setDecodedImageSizeFromElement = (url, element) => {
  if (!url || !element) {
    return null;
  }
  const next = { width: element.naturalWidth, height: element.naturalHeight };
  if (!usable(next)) {
    return null;
  }
  cache.set(url, next);
  return next;
};

/**
 * @param {string} [url]
 * @returns {{width: number, height: number}|null}
 */
export const useDecodedImageSize = url => {
  const [size, setSize] = useState(() => readDecodedImageSize(url));

  useEffect(() => {
    const cached = readDecodedImageSize(url);
    // Re-sync on every url change, INCLUDING to null. Without this a framer
    // switched to a photo whose decode has not landed would keep the previous
    // photo's dimensions and frame the new one against them.
    setSize(cached);
    if (!url || cached || typeof window === 'undefined' || typeof window.Image !== 'function') {
      return undefined;
    }
    let cancelled = false;
    const probe = new window.Image();
    const settle = () => {
      if (cancelled) {
        return;
      }
      const next = { width: probe.naturalWidth, height: probe.naturalHeight };
      if (usable(next)) {
        cache.set(url, next);
        setSize(next);
      }
    };
    probe.onload = settle;
    // A failed decode is not an error condition here: the caller degrades to the
    // variant metadata, which is what it used before this existed.
    probe.onerror = () => {};
    probe.src = url;
    if (probe.complete) {
      settle();
    }
    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [url]);

  return size;
};

/**
 * The same thing for a LIST of urls — the tile strip, which needs one entry per
 * photo and must not call a hook per tile.
 *
 * @param {string[]} urls
 * @returns {Object} url -> {width, height}
 */
export const useDecodedImageSizes = urls => {
  // A stable primitive key, so passing a fresh array of the same urls on every
  // render does not restart the probes.
  const key = (Array.isArray(urls) ? urls : []).filter(Boolean).join('\n');
  const list = useMemo(() => (key ? key.split('\n') : []), [key]);
  const [sizes, setSizes] = useState(() =>
    list.reduce((acc, url) => {
      const hit = readDecodedImageSize(url);
      return hit ? { ...acc, [url]: hit } : acc;
    }, {})
  );

  useEffect(() => {
    const seeded = list.reduce((acc, url) => {
      const hit = readDecodedImageSize(url);
      return hit ? { ...acc, [url]: hit } : acc;
    }, {});
    setSizes(seeded);
    if (typeof window === 'undefined' || typeof window.Image !== 'function') {
      return undefined;
    }
    let cancelled = false;
    const probes = list
      .filter(url => !seeded[url])
      .map(url => {
        const probe = new window.Image();
        const settle = () => {
          if (cancelled) {
            return;
          }
          const next = { width: probe.naturalWidth, height: probe.naturalHeight };
          if (usable(next)) {
            cache.set(url, next);
            setSizes(previous => ({ ...previous, [url]: next }));
          }
        };
        probe.onload = settle;
        probe.onerror = () => {};
        probe.src = url;
        if (probe.complete) {
          settle();
        }
        return probe;
      });
    return () => {
      cancelled = true;
      probes.forEach(probe => {
        probe.onload = null;
        probe.onerror = null;
      });
    };
  }, [list]);

  return sizes;
};
